import { useAutoneTranslation } from '@autone/translations';
import {
  createColumnLoadingGuards,
  DataTableCellContent,
  DataTableCellTitle,
  DataTableHeaderCellWithSorting,
  DataTableIntegerValue,
  type DataTableLoadingObject,
} from '@autone/ui';
import { type ColumnHelper } from '@tanstack/react-table';

import { type APP_NAME } from '../../../consts';

const COLUMN_ID = 'pending_from_production_units' as const;

export type PendingFromProductionColumnId = typeof COLUMN_ID;

interface PendingFromProductionFields {
  pending_from_production_units?: number | null;
}

const { columnCellGuard, accessorFnGuard } =
  createColumnLoadingGuards<PendingFromProductionFields>();

export const createPendingFromProductionColumn = <
  T extends PendingFromProductionFields | DataTableLoadingObject,
>(
  columnHelper: ColumnHelper<T>,
) =>
  columnHelper.accessor(
    accessorFnGuard((row) => row.pending_from_production_units),
    {
      id: COLUMN_ID,
      size: 200,
      header: function PendingFromProductionHeader({ column }) {
        const { t } = useAutoneTranslation<typeof APP_NAME>();

        return (
          <DataTableHeaderCellWithSorting
            column={column}
            arrowPosition="left"
            showSortable
          >
            <DataTableCellContent align="right">
              <DataTableCellTitle className="flex flex-row gap-2 items-center uppercase font-medium whitespace-normal text-right">
                {t('table-columns.pending-from-production-units')}
              </DataTableCellTitle>
            </DataTableCellContent>
          </DataTableHeaderCellWithSorting>
        );
      },
      cell: (ctx) =>
        columnCellGuard({
          ctx,
          renderCell: function PendingFromProductionCell({ getValue }) {
            return (
              <DataTableCellContent align="right">
                <DataTableCellTitle>
                  <DataTableIntegerValue value={getValue()} />
                </DataTableCellTitle>
              </DataTableCellContent>
            );
          },
        }),
    },
  );
